// src/pages/sm/ExceptionManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, Card, Grid, Stack, Tabs, Tab,
    TextField, MenuItem, Select, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
    Alert, Chip, CircularProgress, InputAdornment, Paper, Snackbar, Tooltip,
    FormControlLabel, Checkbox
} from "@mui/material";
import {
    Add as AddIcon,
    EventNote as EventIcon,
    SwapHoriz as SwapIcon,
    FileDownload as DownloadIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { formatDDMMYYYY } from "../../services/datetimeService";

const ExceptionManager = ({ setIsLoggedIn }) => {
    const [tabValue, setTabValue] = useState(0);
    const [staffList, setStaffList] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [compOffs, setCompOffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [compOffLoading, setCompOffLoading] = useState(false);
    const [compOffFilters, setCompOffFilters] = useState({ date_from: '', date_to: '', status: '', employee_search: '' });
    const [debouncedCompOffSearch, setDebouncedCompOffSearch] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Dialog state
    const [openOverrideDialog, setOpenOverrideDialog] = useState(false);
    const [openCompOffDialog, setOpenCompOffDialog] = useState(false);

    // Form states
    const [overrideForm, setOverrideForm] = useState({
        staff_id: null,
        shift_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        reason: ''
    });

    const [compOffForm, setCompOffForm] = useState({
        staff_id: null,
        worked_date: '',
        substitute_off_date: '',
        notes: '',
        allow_override: false
    });

    const fetchStaff = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/staff/staff`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            setStaffList(res.data || []);
        } catch (err) {
            console.error("Error fetching staff:", err);
        }
    }, []);

    const fetchShifts = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/shifts`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) setShifts(res.data.data);
        } catch (err) {
            console.error("Error fetching shifts:", err);
        }
    }, []);

    const fetchOverrides = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/overrides`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) setOverrides(res.data.data);
        } catch (err) {
            console.error("Error fetching overrides:", err);
        }
    }, []);

    const fetchCompOffs = useCallback(async () => {
        try {
            setCompOffLoading(true);
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/compoffs`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') },
                params: {
                    date_from: compOffFilters.date_from || undefined,
                    date_to: compOffFilters.date_to || undefined,
                    status: compOffFilters.status || undefined,
                    employee_search: debouncedCompOffSearch || undefined
                }
            });
            if (res.data.success) setCompOffs(res.data.data);
        } catch (err) {
            console.error("Error fetching compoffs:", err);
            setSnackbar({ open: true, message: 'Failed to load Comp-Off records', severity: 'error' });
        } finally {
            setCompOffLoading(false);
        }
    }, [compOffFilters.date_from, compOffFilters.date_to, compOffFilters.status, debouncedCompOffSearch]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedCompOffSearch(compOffFilters.employee_search.trim()), 350);
        return () => clearTimeout(timer);
    }, [compOffFilters.employee_search]);

    useEffect(() => {
        fetchStaff();
        fetchShifts();
        fetchOverrides();
        fetchCompOffs();
    }, [fetchStaff, fetchShifts, fetchOverrides, fetchCompOffs]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCreateOverride = async () => {
        try {
            setLoading(true);
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/overrides`, overrideForm, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                alert("Override created successfully");
                setOpenOverrideDialog(false);
                fetchOverrides();
            }
        } catch (err) {
            console.error("Error creating override:", err);
            alert(err.response?.data?.message || "Failed to create override");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCompOff = async () => {
        try {
            setLoading(true);
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/compoffs`, compOffForm, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                alert("Comp-off link created successfully");
                setOpenCompOffDialog(false);
                fetchCompOffs();
            }
        } catch (err) {
            console.error("Error creating compoff:", err);
            alert(err.response?.data?.message || "Failed to create comp-off");
        } finally {
            setLoading(false);
        }
    };

    const handleExportCompOff = useCallback(() => {
        if (!compOffs.length) {
            setSnackbar({ open: true, message: 'No Comp-Off records to export', severity: 'warning' });
            return;
        }

        const exportRows = compOffs.map((row) => ({
            'Employee Code': row.employee_number || row.staff_code || '',
            'Staff Name': row.staff_name || '',
            'Worked Date': formatDDMMYYYY(row.worked_date) || '',
            'Substitute Off Date': formatDDMMYYYY(row.substitute_off_date) || '',
            'Status': row.status || '',
            'Notes': row.notes || '',
            'Created By': row.created_by_name || '',
            'Created At': row.created_at ? new Date(row.created_at).toLocaleString('en-GB') : ''
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        worksheet['!cols'] = [
            { wch: 16 }, { wch: 24 }, { wch: 15 }, { wch: 20 },
            { wch: 14 }, { wch: 35 }, { wch: 24 }, { wch: 22 }
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Comp-Off Management');
        const from = compOffFilters.date_from || 'all';
        const to = compOffFilters.date_to || 'all';
        XLSX.writeFile(workbook, `comp_off_management_${from}_${to}.xlsx`);
        setSnackbar({ open: true, message: 'Comp-Off Excel export downloaded', severity: 'success' });
    }, [compOffFilters.date_from, compOffFilters.date_to, compOffs]);

    const overrideColDefs = useMemo(() => [
        { field: "staff_name", headerName: "Staff Member", flex: 2 },
        { field: "shift_code", headerName: "New Shift", flex: 1 },
        {
            headerName: "Dates",
            valueGetter: p => `${formatDDMMYYYY(p.data.effective_from)} - ${formatDDMMYYYY(p.data.effective_to)}`,
            flex: 2
        },
        { field: "reason", headerName: "Reason", flex: 2 },
        {
            field: "created_at",
            headerName: "Created On",
            valueGetter: p => formatDDMMYYYY(p.data.created_at),
            flex: 1
        }
    ], []);

    const handleCancelCompOff = useCallback(async (row) => {
        const reason = window.prompt(
            `Enter cancellation remark / reason for revoking ${row.staff_name}'s Comp-Off (Worked: ${formatDDMMYYYY(row.worked_date)}, Off: ${formatDDMMYYYY(row.substitute_off_date)}):`,
            "Cancelled by HR"
        );
        if (reason === null) return; // User clicked Cancel in prompt

        try {
            setCompOffLoading(true);
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/compoffs/${row.id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') },
                data: { reason: reason.trim() || 'Cancelled by HR' }
            });
            if (res.data.success) {
                setSnackbar({ open: true, message: res.data.message || 'Comp-Off link cancelled successfully', severity: 'success' });
                fetchCompOffs();
            }
        } catch (err) {
            console.error("Error cancelling compoff:", err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to cancel Comp-Off', severity: 'error' });
        } finally {
            setCompOffLoading(false);
        }
    }, [fetchCompOffs]);

    const compOffColDefs = useMemo(() => [
        { field: "employee_number", headerName: "Employee Code", width: 150, valueGetter: p => p.data.employee_number || p.data.staff_code || '-' },
        { field: "staff_name", headerName: "Staff Member", minWidth: 190, flex: 1 },
        {
            field: "worked_date",
            headerName: "Worked Date",
            valueGetter: p => formatDDMMYYYY(p.data.worked_date),
            flex: 1.5
        },
        {
            field: "substitute_off_date",
            headerName: "Substitute Off Date",
            valueGetter: p => formatDDMMYYYY(p.data.substitute_off_date),
            flex: 1.5
        },
        {
            field: "status", headerName: "Status", width: 140, cellRenderer: p => {
                const val = String(p.value || '').toLowerCase();
                let color = 'default';
                if (val === 'approved') color = 'success';
                else if (val === 'cancelled') color = 'error';
                else if (val === 'rejected') color = 'error';
                else if (val === 'submitted' || val === 'in_review') color = 'warning';

                return (
                    <Chip 
                        size="small" 
                        label={String(p.value || '-').toUpperCase()} 
                        color={color} 
                        variant="filled" 
                        sx={{ fontWeight: 800, fontSize: '0.7rem', px: 0.5 }} 
                    />
                );
            }
        },
        { field: "notes", headerName: "Notes", minWidth: 220, flex: 1 },
        { field: "created_by_name", headerName: "Created By", minWidth: 160 },
        { field: "created_at", headerName: "Created On", width: 150, valueGetter: p => formatDDMMYYYY(p.data.created_at) },
        {
            headerName: "Actions",
            width: 150,
            cellRenderer: p => {
                const status = String(p.data.status || '').toLowerCase();
                if (status === 'cancelled' || status === 'rejected') {
                    return <Typography variant="caption" color="text.secondary">—</Typography>;
                }
                return (
                    <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleCancelCompOff(p.data)}
                        sx={{ fontSize: '0.75rem', py: 0.25, px: 1, textTransform: 'none', borderRadius: 1.5, fontWeight: 700 }}
                    >
                        Cancel / Revoke
                    </Button>
                );
            }
        }
    ], [handleCancelCompOff]);

    const compOffSummary = useMemo(() => ({
        total: compOffs.length,
        approved: compOffs.filter(row => String(row.status).toUpperCase() === 'APPROVED').length,
        staff: new Set(compOffs.map(row => row.staff_id)).size
    }), [compOffs]);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <AppHeader onLogout={() => setIsLoggedIn(false)} />
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: '#1e293b' }}>
                    Exception Manager
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab icon={<SwapIcon />} iconPosition="start" label="Shift Overrides" />
                        <Tab icon={<EventIcon />} iconPosition="start" label="Comp-Off Management" />
                    </Tabs>
                </Box>

                {tabValue === 0 && (
                    <Card sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>Active Overrides</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenOverrideDialog(true)}
                                sx={{ backgroundColor: '#2e7d32' }}
                            >
                                New Override
                            </Button>
                        </Stack>
                        <div className="ag-theme-quartz" style={{ height: 500, width: '100%' }}>
                            <AgGridReact
                                rowData={overrides}
                                columnDefs={overrideColDefs}
                                pagination={true}
                                paginationPageSize={10}
                            />
                        </div>
                    </Card>
                )}

                {tabValue === 1 && (
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Comp-Off Management</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    Review, filter and export worked-day to substitute-off links.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Refresh records">
                                    <span>
                                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCompOffs} disabled={compOffLoading} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                            Refresh
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCompOff} disabled={compOffLoading || !compOffs.length} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                    Export Excel
                                </Button>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCompOffDialog(true)} sx={{ backgroundColor: '#2e7d32', textTransform: 'none', fontWeight: 700 }}>
                                    New Comp-Off Link
                                </Button>
                            </Stack>
                        </Stack>

                        <Grid container spacing={2}>
                            {[
                                ['Filtered Records', compOffSummary.total],
                                ['Approved Links', compOffSummary.approved],
                                ['Employees', compOffSummary.staff]
                            ].map(([label, value]) => (
                                <Grid item xs={12} sm={4} key={label}>
                                    <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>{label}</Typography>
                                        <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800, color: '#0f172a' }}>{Number(value).toLocaleString()}</Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #eef2f7' }}>
                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                                <TextField size="small" type="date" label="Worked Date From" InputLabelProps={{ shrink: true }} value={compOffFilters.date_from} onChange={e => setCompOffFilters(prev => ({ ...prev, date_from: e.target.value }))} sx={{ minWidth: 180 }} />
                                <TextField size="small" type="date" label="Worked Date To" InputLabelProps={{ shrink: true }} value={compOffFilters.date_to} onChange={e => setCompOffFilters(prev => ({ ...prev, date_to: e.target.value }))} sx={{ minWidth: 180 }} />
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Status</InputLabel>
                                    <Select label="Status" value={compOffFilters.status} onChange={e => setCompOffFilters(prev => ({ ...prev, status: e.target.value }))}>
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="SUBMITTED">Submitted</MenuItem>
                                        <MenuItem value="IN_REVIEW">In Review</MenuItem>
                                        <MenuItem value="APPROVED">Approved</MenuItem>
                                        <MenuItem value="ACTIVE">Active</MenuItem>
                                        <MenuItem value="REJECTED">Rejected</MenuItem>
                                        <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Employee Code / Name"
                                    value={compOffFilters.employee_search}
                                    onChange={e => setCompOffFilters(prev => ({ ...prev, employee_search: e.target.value }))}
                                    sx={{ minWidth: { xs: '100%', lg: 300 }, flex: 1 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                                />
                            </Stack>
                        </Paper>

                        <Card sx={{ p: 2, borderRadius: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 800 }}>
                                    {compOffLoading ? 'Loading records...' : `${compOffs.length.toLocaleString()} record${compOffs.length === 1 ? '' : 's'}`}
                                </Typography>
                                {compOffLoading && <CircularProgress size={20} />}
                            </Stack>
                            <div className="ag-theme-quartz" style={{ height: 520, width: '100%' }}>
                                <AgGridReact
                                    rowData={compOffs}
                                    columnDefs={compOffColDefs}
                                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                                    pagination={true}
                                    paginationPageSize={25}
                                    animateRows
                                    quickFilterText={compOffFilters.employee_search}
                                    overlayNoRowsTemplate="<span style='color:#64748b'>No Comp-Off records found</span>"
                                />
                            </div>
                        </Card>
                    </Stack>
                )}
            </Box>

            {/* Override Dialog */}
            <Dialog
                open={openOverrideDialog}
                onClose={() => setOpenOverrideDialog(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pt: 2.5, pb: 1, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Create Shift Override</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Override shift schedule for an individual staff member</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        {/* Select Staff Member */}
                        <Autocomplete
                            fullWidth
                            disablePortal
                            options={staffList}
                            getOptionLabel={(option) => `${option.full_name} (${option.employee_number || option.staff_code || option.staff_id})`}
                            isOptionEqualToValue={(option, val) => option.staff_id === val.staff_id}
                            onChange={(e, val) => setOverrideForm({ ...overrideForm, staff_id: val?.staff_id })}
                            componentsProps={{
                                paper: { sx: { minWidth: 380, boxShadow: 3 } }
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.staff_id} sx={{ py: 1.2, px: 2, borderBottom: '1px solid #f1f5f9' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            {option.full_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            {option.employee_number || option.staff_code}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => <TextField {...params} label="Select Staff Member" fullWidth placeholder="Search by name or code..." />}
                        />

                        {/* Override Shift */}
                        <FormControl fullWidth>
                            <InputLabel>Override Shift</InputLabel>
                            <Select
                                label="Override Shift"
                                value={overrideForm.shift_id}
                                onChange={(e) => setOverrideForm({ ...overrideForm, shift_id: e.target.value })}
                            >
                                {shifts.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.shift_code} - {s.shift_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Start Date & End Date Side-by-Side */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Start Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={overrideForm.start_date}
                                onChange={(e) => setOverrideForm({ ...overrideForm, start_date: e.target.value, end_date: e.target.value })}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={overrideForm.end_date}
                                onChange={(e) => setOverrideForm({ ...overrideForm, end_date: e.target.value })}
                            />
                        </Box>

                        {/* Reason / Notes */}
                        <TextField
                            label="Reason / Notes"
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="Enter reason for shift override..."
                            value={overrideForm.reason}
                            onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                    <Button onClick={() => setOpenOverrideDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateOverride}
                        disabled={loading || !overrideForm.staff_id || !overrideForm.shift_id}
                        sx={{
                            backgroundColor: '#2563eb',
                            '&:hover': { backgroundColor: '#1d4ed8' },
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 700,
                            boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                        }}
                    >
                        Save Override
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Comp-Off Dialog */}
            <Dialog
                open={openCompOffDialog}
                onClose={() => setOpenCompOffDialog(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pt: 2.5, pb: 1, px: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Create Comp-Off Link</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Link worked day overtime with substitute off date</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Stack spacing={2.5} sx={{ mt: 0.5 }}>
                        {/* Staff selector — full row */}
                        <Autocomplete
                            fullWidth
                            disablePortal
                            options={staffList}
                            getOptionLabel={(option) => `${option.full_name} (${option.employee_number || option.staff_code || option.staff_id})`}
                            isOptionEqualToValue={(option, val) => option.staff_id === val.staff_id}
                            onChange={(e, val) => setCompOffForm({ ...compOffForm, staff_id: val?.staff_id })}
                            componentsProps={{
                                paper: { sx: { minWidth: 380, boxShadow: 3 } }
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.staff_id} sx={{ py: 1.2, px: 2, borderBottom: '1px solid #f1f5f9' }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                            {option.full_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            {option.employee_number || option.staff_code}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Staff Member" fullWidth placeholder="Search by name or code..." />
                            )}
                        />

                        {/* Date row — side by side */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Date Worked"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={compOffForm.worked_date}
                                onChange={(e) => setCompOffForm({ ...compOffForm, worked_date: e.target.value })}
                            />
                            <TextField
                                label="Substitute Off Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={compOffForm.substitute_off_date}
                                onChange={(e) => setCompOffForm({ ...compOffForm, substitute_off_date: e.target.value })}
                            />
                        </Box>

                        {/* Notes */}
                        <TextField
                            label="Notes / Reason"
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="Enter notes or reason..."
                            value={compOffForm.notes}
                            onChange={(e) => setCompOffForm({ ...compOffForm, notes: e.target.value })}
                        />

                        {/* HR Admin Override Checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={Boolean(compOffForm.allow_override)}
                                    onChange={(e) => setCompOffForm({ ...compOffForm, allow_override: e.target.checked })}
                                    sx={{ color: '#d97706', '&.Mui-checked': { color: '#d97706' } }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309' }}>
                                    Override / Bypass 7-Hour Validation (HR Admin Override)
                                </Typography>
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
                    <Button onClick={() => setOpenCompOffDialog(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateCompOff}
                        disabled={loading || !compOffForm.staff_id || !compOffForm.worked_date || !compOffForm.substitute_off_date}
                        sx={{
                            backgroundColor: '#16a34a',
                            '&:hover': { backgroundColor: '#15803d' },
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 700,
                            boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.2)'
                        }}
                    >
                        Save Link
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ExceptionManager;
